import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';
import { useActivityStripFeed } from 'hooks/useActivityStripFeed';
import { usePageVisible } from 'hooks/usePageVisible';
import { setActivityStrip } from 'reducers/activityStrip';

/**
 * Wires the activity-strip CDN feed (see useActivityStripFeed) into the
 * shared redux slice, gated on page visibility. Was copy-pasted verbatim
 * into both Lobby.tsx and GuestLobby.tsx — any change to this wiring (e.g.
 * the generatedAtMs/Date.now() fix below) had to be applied twice with
 * nothing to catch a miss, and the two lobby test files were themselves
 * near-identical copies for the same reason. Extracted once here instead.
 *
 * Dispatches `updatedAt: activityStripFeed.generatedAtMs` — the DOCUMENT's
 * own generatedAt, NOT `Date.now()` (receive time). useActivityStripFeed
 * already rejects a document whose generatedAt has aged past
 * deriveStaleAfterMs before ever returning it here, so stamping receive
 * time on top of that would double-count staleness: SaloonLobbyContainer's
 * own staleness check (against this same updatedAt) would then only trip
 * after roughly TWICE deriveStaleAfterMs's threshold, not once.
 *
 * Reads `retryToken` from the SAME redux slice this hook writes to, rather
 * than exposing a retry function of its own: ActivityStrip's Retry action
 * (only rendered while status is 'unavailable') is several components below
 * wherever this hook is mounted (Lobby.tsx/GuestLobby.tsx), and dispatching
 * requestActivityStripRetry() from there — a component already connected to
 * redux via SaloonLobbyContainer — avoids threading a callback down through
 * every intermediate layer. useActivityStripFeed's retrySignal effect
 * dependency then does the actual work of restarting the poll.
 */
export function useActivityStripDispatch(): void {
  const dispatch = useAppDispatch();
  const isPageVisible = usePageVisible();
  const retryToken = useAppSelector((state: any) => state.activityStrip?.retryToken) as number;
  const activityStripFeed = useActivityStripFeed(isPageVisible, retryToken);

  useEffect(() => {
    if (!activityStripFeed) return;
    dispatch(
      setActivityStrip({
        metrics: activityStripFeed.metrics,
        updatedAt: activityStripFeed.generatedAtMs,
        maxAgeMs: activityStripFeed.maxAgeMs,
        clockOffsetMs: activityStripFeed.clockOffsetMs
      })
    );
  }, [activityStripFeed, dispatch]);
}
